import React from "react";
import {
    Grid,
    GridColumn,
    GridRow,
    LabeledDate,
    LabeledText,
    ListTable,
    createListTableColumns,
    renderTextWithTooltip
} from "sprint-common-web";

import { RecordType } from "../../../../common/types/RecordType";
import { renderRecordType } from "../../../../common/utils/renderRecordType";
import { OrderRequestSectionWrapper } from "../components/OrderRequestSectionWrapper";
import { OrderRequestDashboardItem } from "../OrderRequestDashboardItem";

interface OrderRequestDeliverySectionProps {
    minHeight: number;
}

interface OrderRequestFileNamesItem {
    type: RecordType;
    fileName: string;
}

export const OrderRequestDeliverySection = ({ minHeight }: OrderRequestDeliverySectionProps): JSX.Element => {
    return (
        <OrderRequestSectionWrapper
            header="Delivery"
            style={{ minHeight, flex: 1 }}
            testId="order-request-delivery-section"
        >
            {(orderRequest) => {
                const fileItems = mapToOrderRequestFileNameItems(orderRequest);
                const columns = createListTableColumns<OrderRequestFileNamesItem>([
                    {
                        type: "basic",
                        key: "type",
                        name: "Type",
                        minWidth: 38,
                        maxWidth: 50,
                        onRender: renderRecordType
                    },
                    {
                        type: "basic",
                        key: "fileName",
                        name: "Target filename",
                        minWidth: 38,
                        maxWidth: 150,
                        onRender: renderTextWithTooltip
                    }
                ]);
                return (
                    <Grid>
                        <GridRow>
                            <GridColumn lg={4}>
                                <LabeledDate
                                    label="Due date"
                                    testId="order-request-dueDate"
                                    value={new Date(orderRequest.dueDate)}
                                />
                            </GridColumn>
                            <GridColumn lg={4}>
                                <LabeledText
                                    label="Delivery to"
                                    testId="order-request-deliveryTo"
                                    value={orderRequest.deliveryTo}
                                />
                            </GridColumn>
                            <GridColumn lg={4}>
                                <LabeledText
                                    label="Delivery details"
                                    testId="order-request-deliveryDetails"
                                    value={orderRequest.deliveryDetails}
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow>
                            <GridColumn lg={12}>
                                <ListTable
                                    columns={columns}
                                    items={fileItems}
                                    isLoading={false}
                                    shimmerLines={1}
                                    noItemsMessage="No filenames"
                                />
                            </GridColumn>
                        </GridRow>
                    </Grid>
                );
            }}
        </OrderRequestSectionWrapper>
    );
};

const mapToOrderRequestFileNameItems = (orderRequest?: OrderRequestDashboardItem): OrderRequestFileNamesItem[] => {
    if (!orderRequest) return [];

    const items: OrderRequestFileNamesItem[] = [];

    if (orderRequest.cloudAssets.video.targetFilename) {
        items.push({
            type: RecordType.Video,
            fileName: orderRequest.cloudAssets.video.targetFilename
        });
    }

    const subtitleItems = orderRequest.cloudAssets.subtitles
        .filter((subtitle) => subtitle.targetFilename)
        .map((subtitle) => ({
            type: RecordType.Subtitle,
            fileName: subtitle.targetFilename ?? "-"
        }));

    items.push(...subtitleItems);

    return items;
};
